<script setup lang="ts">
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { STAFF_ANCHORS, STAFF_CONTEXTS } from '@/modules/onboarding/scenarios/staff/anchors'
import { ERouteNames } from '@/router/ERouteNames'
import DepartmentStructureTab from './DepartmentStructureTab.vue'
import PositionStructureTab from './PositionStructureTab.vue'

const STAFF_SETTINGS_TABS = ['departments', 'positions'] as const
type TStaffSettingsTab = typeof STAFF_SETTINGS_TABS[number]

const route = useRoute()
const router = useRouter()
const permissionsStore = usePermissionsStore()

const canDepartments = computed(() => Boolean(permissionsStore.modules.employee_management?.department_edit))
const canPositions = computed(() => Boolean(permissionsStore.modules.employee_management?.position_edit))

// Онбординг заходит сюда из таблицы сотрудников и по «Назад» возвращается туда же
useOnboardingContext({
  names: [STAFF_CONTEXTS.settings],
  root: () => document.body,
  actions: {
    backToStaff: async () => { await router.push({ name: ERouteNames.STAFF }) },
  },
})

function staffTabFromQuery(): TStaffSettingsTab {
  const q = route.query.tab
  if (typeof q === 'string' && (STAFF_SETTINGS_TABS as readonly string[]).includes(q)) {
    return q as TStaffSettingsTab
  }
  return 'departments'
}

const tab = ref<TStaffSettingsTab>(staffTabFromQuery())

watch([canDepartments, canPositions], () => {
  if (tab.value === 'departments' && !canDepartments.value && canPositions.value)
    tab.value = 'positions'
  if (tab.value === 'positions' && !canPositions.value && canDepartments.value)
    tab.value = 'departments'
}, { immediate: true })

onMounted(async () => {
  if (!route.query.tab) {
    await router.replace({ query: { ...route.query, tab: tab.value } }).catch((err: unknown) => {
      if ((err as { name?: string }).name !== 'NavigationDuplicated') {
        console.error(err)
      }
    })
  }
})

watch(tab, async (newTab, oldTab) => {
  if (oldTab && newTab !== oldTab && route.query.tab !== newTab) {
    await router.replace({ query: { ...route.query, tab: newTab } }).catch((err: unknown) => {
      if ((err as { name?: string }).name !== 'NavigationDuplicated') {
        console.error(err)
      }
    })
  }
})

watch(() => route.query.tab, newTabQuery => {
  if (typeof newTabQuery === 'string' && (STAFF_SETTINGS_TABS as readonly string[]).includes(newTabQuery)) {
    if (tab.value !== newTabQuery) {
      tab.value = newTabQuery as TStaffSettingsTab
    }
  }
})
</script>

<template>
  <div class="d-flex flex-column ga-4">
    <VTabs
      v-if="canDepartments || canPositions"
      v-model="tab"
      color="primary"
      style="width: max-content; max-width: 100%; flex-shrink: 0;"
      :data-onboarding="STAFF_ANCHORS.settings.tabs"
    >
      <VTab v-if="canDepartments" value="departments">Структура МО</VTab>
      <VTab v-if="canPositions" value="positions">Должности</VTab>
    </VTabs>

    <div v-if="!canDepartments && !canPositions" class="text-medium-emphasis">
      Недостаточно прав для настроек модуля.
    </div>
    <DepartmentStructureTab v-else-if="tab === 'departments' && canDepartments" :data-onboarding="STAFF_ANCHORS.settings.content" />
    <PositionStructureTab v-else-if="tab === 'positions' && canPositions" :data-onboarding="STAFF_ANCHORS.settings.content" />
  </div>
</template>
